import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { LogOut, GraduationCap, Calendar, Key, User } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const [adminEmail, setAdminEmail] = useState('');
  const [courses, setCourses] = useState([]);
  const [events, setEvents] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    const email = localStorage.getItem('adminEmail');

    if (!token) {
      navigate('/admin');
      return;
    }

    setAdminEmail(email);
    fetchData(token);
  }, [navigate]);

  const fetchData = async (token) => {
    try {
      const [coursesRes, eventsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/courses`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${BACKEND_URL}/api/admin/events`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setCourses(coursesRes.data.courses || []);
      setEvents(eventsRes.data.events || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 401) {
        handleLogout();
      }
    }
  };

  const handleLogout = async () => {
    const token = localStorage.getItem('adminToken');
    try {
      await axios.post(`${BACKEND_URL}/api/admin/logout`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Logout error:', error);
    }

    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminEmail');
    navigate('/admin');
  };

  const activeCourses = courses.filter(c => c.status === 'active').length;
  const currentEvents = events.filter(e => e.status === 'current').length;

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <div className="bg-white shadow-md">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="Target Health" className="h-12" />
              <div>
                <h1 className="text-2xl font-bold text-slate-800">Admin Dashboard</h1>
                <p className="text-sm text-slate-600">Target for Health Consulting</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-slate-700">
                <User size={20} />
                <span>{adminEmail}</span>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg transition-colors"
              >
                <LogOut size={20} />
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-600 text-sm">Total Courses</p>
                <p className="text-3xl font-bold text-cyan-600">{courses.length}</p>
              </div>
              <GraduationCap size={40} className="text-cyan-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-600 text-sm">Active Courses</p>
                <p className="text-3xl font-bold text-green-600">{activeCourses}</p>
              </div>
              <GraduationCap size={40} className="text-green-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-600 text-sm">Total Events</p>
                <p className="text-3xl font-bold text-orange-600">{events.length}</p>
              </div>
              <Calendar size={40} className="text-orange-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-600 text-sm">Current Events</p>
                <p className="text-3xl font-bold text-purple-600">{currentEvents}</p>
              </div>
              <Calendar size={40} className="text-purple-500" />
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid md:grid-cols-3 gap-6">
          <Link
            to="/admin/courses"
            className="bg-gradient-to-br from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white rounded-lg shadow-lg p-8 transition-all transform hover:scale-105"
          >
            <GraduationCap size={48} className="mb-4" />
            <h3 className="text-2xl font-bold mb-2">Manage Courses</h3>
            <p className="text-cyan-100">Add, edit, or delete training courses</p>
          </Link>

          <Link
            to="/admin/events"
            className="bg-gradient-to-br from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-lg shadow-lg p-8 transition-all transform hover:scale-105"
          >
            <Calendar size={48} className="mb-4" />
            <h3 className="text-2xl font-bold mb-2">Manage Events</h3>
            <p className="text-orange-100">Add, edit, or delete events</p>
          </Link>

          <Link
            to="/admin/settings"
            className="bg-gradient-to-br from-slate-600 to-slate-700 hover:from-slate-700 hover:to-slate-800 text-white rounded-lg shadow-lg p-8 transition-all transform hover:scale-105"
          >
            <Key size={48} className="mb-4" />
            <h3 className="text-2xl font-bold mb-2">Settings</h3>
            <p className="text-slate-300">Change password and preferences</p>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;